import { DateObjectType } from '@typings/common';
import dayjs from 'dayjs';

export const avatarSize = (size: string) => {
  switch (true) {
    case window.innerWidth < 768 && size === 'small':
      return 80;
    case window.innerWidth < 768 && size === 'large':
      return 120;
    case window.innerWidth < 1024 && size === 'small':
      return 100;
    case window.innerWidth < 1024 && size === 'large':
      return 140;
    case window.innerWidth < 1768 && size === 'small':
      return 120;
    case window.innerWidth < 1768 && size === 'large':
      return 160;
    case window.innerWidth >= 1768 && size === 'small':
      return 160;
    case window.innerWidth >= 1768 && size === 'large':
      return 200;
    default:
      return 80;
  }
};

export const acceptedFileTypes = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/svg+xml',
];

export const acceptedMultiFileTypes = [
  'application/pdf', // PDF files
  'application/msword', // DOC files
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX files
  'application/rtf', // RTF files
  'text/plain', // TXT files
  'application/octet-stream', // Added for extra security
  'image/jpeg', // JPEG images
  'image/png', // PNG images
  'image/jpg', /// JPG images
];

export const acceptedLocalMediaFile = [
  // Images
  'image/jpeg', // .jpeg, .jpg
  'image/jpg', // .jpg (optional, some consider it redundant)
  'image/png', // .png

  // Documents
  'application/pdf', // .pdf
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx

  // Videos
  'video/mp4', // .mp4
  'video/quicktime', // .mov
  'video/x-msvideo', // .avi
  'video/x-ms-wmv', // .wmv
  'video/x-matroska', // .mkv

  // Audio
  'audio/mpeg', // .mp3
  'audio/wav', // .wav
  'audio/aac', // .aac
  'audio/flac', // .flac
];

export const capitalizeName = (name: string) => {
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const isValidEmail = (email: string | undefined): boolean => {
  if (email?.trim()) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
  return true;
};

const numericRegExp = /^\d+$/;

export const dayIsValid = (day: any) => {
  return numericRegExp.test(day) && Number(day) >= 1 && Number(day) <= 31;
};

export const monthIsValid = (month: any) => {
  return numericRegExp.test(month) && Number(month) >= 1 && Number(month) <= 12;
};

export const yearIsValid = (year: any) => {
  return (
    numericRegExp.test(year) &&
    Number(year) > 1900 &&
    Number(year) <= new Date().getFullYear()
  );
};

export const truncateFileName = (name: string, maxLength: number) => {
  if (name.length <= maxLength) return name;
  const start = name.slice(0, Math.ceil(maxLength / 2));
  const end = name.slice(-Math.floor(maxLength / 2));
  return `${start}...${end}`;
};

export const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const updateErrors = (
  prevErrors: { id: string; message: string }[],
  fieldId: string,
  errorMessage: string | null
) => {
  const newErrors = [...prevErrors];
  const existingIndex = newErrors.findIndex(error => error.id === fieldId);

  if (errorMessage) {
    if (existingIndex !== -1) {
      // Update existing error
      newErrors[existingIndex] = { id: fieldId, message: errorMessage };
    } else {
      // Add new error
      newErrors.push({ id: fieldId, message: errorMessage });
    }
  } else if (existingIndex !== -1) {
    // Remove the error if errorMessage is null or empty
    newErrors.splice(existingIndex, 1);
  }

  return newErrors;
};

export const getErrorMessage = (
  errors: { id: string; message: string }[],
  fieldId: string
): string | undefined => {
  return errors.find(err => err.id === fieldId)?.message;
};

export const formatDate = (
  date: DateObjectType,
  targetFormat: string
): string => {
  if (!date) return '';

  const { year, month, day } = date;
  if (!year || !month || !day) {
    return '';
  }

  const dateString = `${year}-${month}-${day}`;
  const parsedDate = dayjs(dateString);

  if (!parsedDate.isValid()) {
    return '';
  }

  const result = parsedDate.format(targetFormat);
  return result;
};

export const parseFormattedDate = (formattedDate: string): DateObjectType => {
  if (!formattedDate) {
    return { year: '', month: '', day: '' };
  }
  const date = dayjs(formattedDate);

  return {
    year: date.isValid() ? date.year().toString() : '',
    month: date.isValid() ? (date.month() + 1).toString().padStart(2, '0') : '',
    day: date.isValid() ? date.date().toString().padStart(2, '0') : '',
  };
};

export const formatPossessive = (name: string) => {
  if (!name) return '';

  if (name.endsWith('s')) {
    return `${name}'`;
  }
  return `${name}'s`;
};

export const validatePastDateMessage = (date: DateObjectType): string => {
  // Safely trim and extract values
  const day = (date?.day || '').trim();
  const month = (date?.month || '').trim();
  const year = (date?.year || '').trim();

  const isAnyFieldFilled = day || month || year;

  if (isAnyFieldFilled) {
    // Ensure all fields are filled
    if (!day || !month || !year) {
      return 'Please enter a valid date';
    }

    // Construct the padded date string (e.g., "2025-03-31")
    const paddedDate = `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

    const enteredDate = dayjs(paddedDate, 'YYYY-MM-DD', true);

    if (!enteredDate.isValid()) {
      return 'Please enter a valid date';
    }

    const today = dayjs().startOf('day');

    if (enteredDate.isAfter(today)) {
      return 'You cannot enter a date in the future';
    }
  }

  return '';
};

export interface CommonRadioType {
  value: string;
  label: string;
}

export const commonRadioOptions: CommonRadioType[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'notSure', label: 'Not Sure' },
];

export const successMessageInMiddleOfSteps = {
  title: 'Your progress has been saved',
  content: 'Please continue below',
};

export const truncateEmail = (email: string, maxLength = 20) => {
  if (email.length <= maxLength) return email;

  const [user, domain] = email.split('@');
  const charsToShow = maxLength - domain.length - 4;

  if (charsToShow <= 0) {
    return `...@${domain}`;
  }

  return `${user.substring(0, charsToShow)}...@${domain}`;
};
